/**
 * feat-235 — pure derivations over the Tracks payload. Kept out of the
 * components so they can be tested without a DOM, and so the two places that ask
 * the same question (the prereq picker and the Lessons tab's membership strip)
 * cannot answer it differently.
 */

import type { LearnTrack } from '../../../../services/quizContentAdminService';

/** `edges[a]` = the set of tracks `a` REQUIRES. */
export type PrereqEdges = Record<string, Set<string>>;

export function prereqEdges(tracks: LearnTrack[]): PrereqEdges {
  const edges: PrereqEdges = {};
  for (const t of tracks) edges[t.id] = new Set(t.prereq_track_ids);
  return edges;
}

/**
 * Can `from` reach `to` by following "requires" edges? Iterative, and it marks
 * nodes seen — an already-cyclic graph (one the server rejected before, or one
 * written by an older client) must not hang the editor.
 */
export function requiresTransitively(
  edges: PrereqEdges,
  from: string,
  to: string,
): boolean {
  const seen = new Set<string>();
  const stack = Array.from(edges[from] ?? []);
  while (stack.length > 0) {
    const node = stack.pop() as string;
    if (node === to) return true;
    if (seen.has(node)) continue;
    seen.add(node);
    stack.push(...Array.from(edges[node] ?? []));
  }
  return false;
}

export interface PrereqOption {
  track: LearnTrack;
  checked: boolean;
  /** Non-null ⇒ the option is disabled and this is the reason shown ON the row.
   *  Cycles are SHOWN, not hidden (mockup §4): a hidden option is
   *  indistinguishable from a bug, and the author is the person who can fix the
   *  actual conflict. The API still answers 400; this stops it being reached. */
  blockedReason: string | null;
}

/**
 * The prerequisite picker's rows for `track`, in track order, with the two
 * refusals the DATABASE CANNOT EXPRESS pre-explained:
 *   * self-reference
 *   * a cycle at any depth — evaluated against the graph the write would produce
 */
export function prereqOptions(track: LearnTrack, tracks: LearnTrack[]): PrereqOption[] {
  const edges = prereqEdges(tracks);
  const current = new Set(track.prereq_track_ids);
  return tracks.map((candidate) => {
    let blockedReason: string | null = null;
    if (candidate.id === track.id) {
      blockedReason = "a track can't require itself";
    } else if ((edges[candidate.id] ?? new Set()).has(track.id)) {
      blockedReason = `${candidate.title || '(untitled)'} already requires ${track.title || '(untitled)'}`;
    } else if (requiresTransitively(edges, candidate.id, track.id)) {
      blockedReason = `${candidate.title || '(untitled)'} already requires ${track.title || '(untitled)'} indirectly`;
    }
    return { track: candidate, checked: current.has(candidate.id), blockedReason };
  });
}

export interface LessonTrackMembership {
  track: LearnTrack;
  /** 1-based position of the lesson WITHIN THIS TRACK. Per-track by design (D5 +
   *  D6): moving a shared lesson in one track does not move it in another. */
  position: number;
}

/**
 * Every track a lesson belongs to, with its position in each. Drives the Lessons
 * tab's read-only membership strip — the surface that makes "this lesson is in
 * three tracks" legible from the lesson you are editing.
 */
export function lessonMemberships(
  lessonId: string,
  tracks: LearnTrack[],
): LessonTrackMembership[] {
  const out: LessonTrackMembership[] = [];
  for (const track of tracks) {
    const idx = track.lesson_ids.indexOf(lessonId);
    if (idx >= 0) out.push({ track, position: idx + 1 });
  }
  return out;
}

/** Ordinal shown on a membership row and in the strip: 1 → "1st", 2 → "2nd", … */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

// ===========================================================================
// feat-242 — the curriculum Map's derivations: tiers, edges, four hazards.
//
// These live HERE, beside `prereqEdges` / `requiresTransitively`, because a
// SECOND walker that disagrees with the picker about "does A require B" is the
// defect slice 3 avoided by feeding all three of its call sites from one
// catalog. Everything below is built on the two primitives above; nothing below
// re-implements a graph walk, and in particular nothing below re-implements
// `requiresTransitively`'s seen-set — which is what makes the Map inherit its
// termination guarantee on an already-cyclic payload rather than hoping for one
// (AC12).
//
// Pure and DOM-free on purpose: the largest correctness surface in this slice is
// testable without a renderer.
// ===========================================================================

/** The only thing the Map needs to know about a Lesson. `QuizUnit` satisfies it
 *  structurally, so callers pass their `units` array unchanged. */
export interface MapLesson {
  id: string;
  active: boolean;
}

/**
 * `gating` — the evaluator acts on it, and it is the ONLY kind that assigns tiers.
 * `nongating` — a stored prereq row the evaluator ignores. Drawn, and counted under H3.
 * `loop` — an edge between two members of one loop group (H4).
 */
export type MapEdgeKind = 'gating' | 'nongating' | 'loop';

export interface MapEdge {
  /** The REQUIRED track — the arrow's TAIL. */
  from: string;
  /** The REQUIRING track — the arrow's HEAD. */
  to: string;
  kind: MapEdgeKind;
}

/**
 * An edge is GATING iff BOTH hold (spec "Which edges are drawn"):
 *
 *   requiring.access_mode === 'prereq'   AND   required.active
 *
 * Both clauses are verified backend behaviour, not style. `evaluate` only
 * consults prereqs under `ACCESS_PREREQ`, and `gating_ids` drops a Hidden
 * required track entirely — "an inactive track is not a route TO content".
 * Every other stored row is real, ignored, and drawn non-gating.
 */
export function isGatingEdge(requiring: LearnTrack, required: LearnTrack): boolean {
  return requiring.access_mode === 'prereq' && required.active;
}

/**
 * Every stored prereq row as a drawable edge, classified.
 *
 * A row naming a track that is not in the payload is DROPPED, not drawn: AC6
 * asserts an edge's endpoints resolve to the bounding boxes of the two nodes it
 * names, and an edge to a node that was never rendered cannot satisfy that. It
 * would also be the one edge on the canvas whose target the author cannot see.
 */
export function mapEdges(tracks: LearnTrack[]): MapEdge[] {
  const byId: Record<string, LearnTrack> = {};
  for (const t of tracks) byId[t.id] = t;
  const inLoop = loopTrackIds(tracks);

  const out: MapEdge[] = [];
  for (const requiring of tracks) {
    for (const requiredId of requiring.prereq_track_ids) {
      const required = byId[requiredId];
      if (!required) continue;
      // A loop edge is told apart FIRST: inside a loop group the gating
      // question is moot, because no member can be reached through the others.
      const kind: MapEdgeKind =
        inLoop.has(requiring.id) && inLoop.has(required.id)
          ? 'loop'
          : isGatingEdge(requiring, required)
            ? 'gating'
            : 'nongating';
      out.push({ from: required.id, to: requiring.id, kind });
    }
  }
  return out;
}

// --- H4: loops ------------------------------------------------------------

/**
 * Every track that can reach ITSELF by following "requires" — i.e. sits on a
 * loop. Evaluated over ALL stored prereq rows, not gating ones only, because
 * that is the graph the write handler's cycle check runs on (`prereqOptions`
 * blocks against the same edge set) and therefore the graph a cycle can only
 * ever appear in. Using the gating subset here would let an `open`-mode loop
 * through, and the tier walk below would then have a cycle to fall into.
 */
export function loopTrackIds(tracks: LearnTrack[]): Set<string> {
  const edges = prereqEdges(tracks);
  const out = new Set<string>();
  for (const t of tracks) {
    if (requiresTransitively(edges, t.id, t.id)) out.add(t.id);
  }
  return out;
}

/**
 * Loop members partitioned into groups — two tracks share a group iff each can
 * reach the other (the strongly-connected component). Groups keep payload order
 * both within and between themselves, so the canvas does not reshuffle between
 * renders.
 *
 * H4 marks a GROUP, not an edge: "N Tracks require each other in a loop."
 */
export function loopGroups(tracks: LearnTrack[]): string[][] {
  const edges = prereqEdges(tracks);
  const members = tracks.filter((t) => requiresTransitively(edges, t.id, t.id));
  const groups: string[][] = [];
  const placed = new Set<string>();
  for (const t of members) {
    if (placed.has(t.id)) continue;
    const group = members
      .filter(
        (u) =>
          u.id === t.id ||
          (!placed.has(u.id) &&
            requiresTransitively(edges, t.id, u.id) &&
            requiresTransitively(edges, u.id, t.id)),
      )
      .map((u) => u.id);
    for (const id of group) placed.add(id);
    groups.push(group);
  }
  return groups;
}

// --- Tiers ----------------------------------------------------------------

export interface TrackTiers {
  /** `tiers[n]` = the track ids in tier n, in payload order. */
  tiers: string[][];
  /** Tier index per track. A loop member is ABSENT — it has no tier. */
  tierOf: Record<string, number>;
  /** H4's groups, rendered after the last tier column. */
  loopGroups: string[][];
}

/**
 * Dependency tiers, left to right: tier 0 requires nothing, tier N requires
 * something in tier N−1. **Gating edges only** — tiering on every stored row
 * would draw a graph the evaluator does not believe in (spec, Assumption 3).
 *
 * A LOOP MEMBER GETS NO TIER, and that is the design ruling, not a fallback
 * (mockup S7 note 4): tier assignment over a cyclic component has no answer, and
 * inventing one — tier 0, or "wherever the walk stopped" — draws a graph that is
 * quietly false. The whole component is lifted into `loopGroups` and rendered
 * after the last column. This is also what keeps AC12 honest: the walk below
 * terminates because it never ENTERS a cycle, not because a depth limit caught
 * it.
 *
 * Corollary, and the one imprecision worth knowing about: a non-loop track whose
 * only gating requirement is a loop member has no tiered prereq left after the
 * lift, so it lands in tier 0 under a heading that reads "requires nothing". Its
 * edge is still drawn, into the loop group, so the reason is on screen. Left as
 * is deliberately — the write handler rejects cycles with a 400 and
 * `prereqOptions` blocks them in the picker, so this is reachable only from a
 * payload no supported client can author, and every alternative labelling is
 * false in a different place.
 */
export function trackTiers(tracks: LearnTrack[]): TrackTiers {
  const groups = loopGroups(tracks);
  const inLoop = new Set<string>();
  for (const g of groups) for (const id of g) inLoop.add(id);

  const byId: Record<string, LearnTrack> = {};
  for (const t of tracks) byId[t.id] = t;

  /** Gating requirements, over the ACYCLIC remainder only. */
  const requires: Record<string, string[]> = {};
  for (const t of tracks) {
    if (inLoop.has(t.id)) continue;
    requires[t.id] = t.prereq_track_ids.filter((id) => {
      const required = byId[id];
      return !!required && !inLoop.has(id) && isGatingEdge(t, required);
    });
  }

  // Longest-path layering, memoised. The graph is acyclic by construction (every
  // cyclic node was lifted out above), so `resolving` can only ever catch a bug
  // in that claim — it is a tripwire, not the termination mechanism.
  const tierOf: Record<string, number> = {};
  const resolving = new Set<string>();
  const tierFor = (id: string): number => {
    if (tierOf[id] !== undefined) return tierOf[id];
    if (resolving.has(id)) return 0;
    resolving.add(id);
    let tier = 0;
    for (const requiredId of requires[id] ?? []) {
      tier = Math.max(tier, tierFor(requiredId) + 1);
    }
    resolving.delete(id);
    tierOf[id] = tier;
    return tier;
  };

  const tiers: string[][] = [];
  for (const t of tracks) {
    if (inLoop.has(t.id)) continue;
    const tier = tierFor(t.id);
    while (tiers.length <= tier) tiers.push([]);
    tiers[tier].push(t.id);
  }

  return { tiers, tierOf, loopGroups: groups };
}

// --- H1: Lessons nobody can open -----------------------------------------

/**
 * The H1 predicate, all three clauses (spec §4, and it is a correctness
 * requirement that all three are here):
 *
 *   lesson.active
 *     AND  no ACTIVE track contains it   (zero tracks, OR every one is Hidden)
 *     AND  the tenant has >= 2 active tracks
 *
 * Clause 1 protects the shipped ruling — a Hidden Lesson is the draft, never
 * marked. Clause 2 is broader than "in no track": `track_ids_by_lesson` is held
 * over active tracks only, so a Lesson filed ONLY into Hidden tracks is exactly
 * as unreachable as one filed nowhere, and the literal test misses it. Clause 3
 * is D3 — with one active track the phone serves the flat Lesson list, so an
 * unfiled Lesson IS in front of the crew. **That clause is why Ladle & Leaf can
 * never see this mark (AC8).**
 *
 * NOTE the population here is deliberately NOT the library footer's. The footer
 * counts `unassigned_lesson_ids` — no track AT ALL — and this set also contains
 * the hidden-only Lessons. The two numbers differ legitimately and must never be
 * added together (spec §5's population note, OPEN-QUESTIONS Q4).
 */
export function unreachableLessonIds(
  lessons: MapLesson[],
  tracks: LearnTrack[],
): Set<string> {
  const out = new Set<string>();
  const activeTracks = tracks.filter((t) => t.active);
  if (activeTracks.length < 2) return out;

  const reachable = new Set<string>();
  for (const t of activeTracks) for (const id of t.lesson_ids) reachable.add(id);

  for (const lesson of lessons) {
    if (!lesson.active) continue;
    if (reachable.has(lesson.id)) continue;
    out.add(lesson.id);
  }
  return out;
}

// --- H2: dead-end prereq chains ------------------------------------------

/**
 * Tracks with zero On Lessons — H2's CAUSE, rendered in the named track's own
 * body so the cause is on screen with the effect.
 *
 * Both arms of AC9 land here: a track with no `lesson_ids` at all, and a track
 * whose Lessons are all `active: false`. Slice 3's rule 2 makes no distinction —
 * either way the track is `available`, never `completed`, and satisfies no
 * prereq. That is fail-closed ON PURPOSE, so an empty track cannot vacuously
 * open stations whose content does not exist yet.
 */
export function tracksWithNoOnLessons(
  tracks: LearnTrack[],
  lessons: MapLesson[],
): Set<string> {
  const isOn = new Set(lessons.filter((l) => l.active).map((l) => l.id));
  const out = new Set<string>();
  for (const t of tracks) {
    if (!t.lesson_ids.some((id) => isOn.has(id))) out.add(t.id);
  }
  return out;
}

export interface DeadEndChain {
  /** The track carrying the mark. */
  trackId: string;
  /** Shortest gating path from `trackId` to the empty track, EXCLUDING
   *  `trackId` itself. Its last element is the empty track. A single element is
   *  the one-hop case; two or more is AC10's transitive case, and the copy
   *  differs between them. */
  path: string[];
}

/**
 * H2 — every track whose gating requirement chain reaches a track with zero On
 * Lessons, with the shortest such chain so the mark can NAME it.
 *
 * Walked over GATING edges only: a requirement the evaluator ignores cannot
 * dead-end anything, and a Hidden required track is already dropped from the
 * gating set, so it never produces a false H2.
 *
 * A track is never marked for being empty ITSELF — that is the cause box in its
 * own body, not a claim about what sits behind it. AC10 is the reason this is a
 * BFS rather than a one-hop check: `C` requires `B` requires empty `E` must mark
 * `C` too, and a one-hop implementation passes AC9 and fails exactly here.
 */
export function deadEndChains(
  tracks: LearnTrack[],
  lessons: MapLesson[],
): DeadEndChain[] {
  const empty = tracksWithNoOnLessons(tracks, lessons);
  const byId: Record<string, LearnTrack> = {};
  for (const t of tracks) byId[t.id] = t;

  const gatingRequires = (id: string): string[] => {
    const t = byId[id];
    if (!t) return [];
    return t.prereq_track_ids.filter((requiredId) => {
      const required = byId[requiredId];
      return !!required && isGatingEdge(t, required);
    });
  };

  const out: DeadEndChain[] = [];
  for (const start of tracks) {
    // Breadth-first, marking nodes seen — same property `requiresTransitively`
    // holds, for the same reason: a cyclic payload must not hang the editor.
    const seen = new Set<string>([start.id]);
    let frontier: string[][] = gatingRequires(start.id).map((id) => [id]);
    let found: string[] | null = null;
    while (frontier.length > 0 && !found) {
      const next: string[][] = [];
      for (const path of frontier) {
        const head = path[path.length - 1];
        if (seen.has(head)) continue;
        seen.add(head);
        if (empty.has(head)) {
          found = path;
          break;
        }
        for (const requiredId of gatingRequires(head)) {
          if (!seen.has(requiredId)) next.push([...path, requiredId]);
        }
      }
      frontier = next;
    }
    if (found) out.push({ trackId: start.id, path: found });
  }
  return out;
}

// --- H3: requirements nothing acts on ------------------------------------

export interface IgnoredRequirement {
  /** The track carrying the mark — the one doing the requiring. */
  trackId: string;
  /** `open_mode` is H3(b), `hidden_required` is H3(a). When both are true of one
   *  track, `open_mode` wins: "you set this to Anyone" is the fact that explains
   *  the whole row, and the Hidden-ness of the target is downstream of it. */
  reason: 'open_mode' | 'hidden_required';
  /** Stored prereq rows the evaluator does not act on. Never empty. */
  ignoredTrackIds: string[];
  /** The prereqs that ARE acted on — H3(a)'s copy says "only Y is acted on", so
   *  the mark needs them. Empty under `open_mode`, where nothing is acted on. */
  gatingTrackIds: string[];
}

/**
 * H3 — stored prereq rows the slice-3 evaluator ignores. Two sub-cases, both
 * verified in shipped code, both silent today:
 *
 *  (a) the required track is HIDDEN — `gating_ids` drops it (revision 8, rule 3).
 *  (b) the requiring track's `access_mode` is `open` — `evaluate` only consults
 *      prereqs under `ACCESS_PREREQ`, and `TrackDetailPane` merely HIDES the
 *      prereq list under `open` rather than clearing the rows, so `prereq` →
 *      tick → switch to `open` leaves them stored and invisible.
 *
 * On (b): the shipped comment in `TrackDetailPane.tsx` asserts an `open` track
 * "can never carry orphan prereq rows the slice-3 evaluator would ignore". That
 * comment is FALSE as written — it is true only of the narrow claim that you
 * cannot AUTHOR a new row while in `open` mode. Traced on both sides: the mode
 * radio fires a single-field `updateTrack({access_mode})` patch, and the backend
 * answers it with `_apply_update("learn_track", …)`, a plain column patch that
 * never touches `learn_track_prereq`. **The Map is the only surface on which
 * those rows become visible again**, which is a point in this feature's favour.
 * The comment is NOT corrected here — `feat-240` is rewriting that file right
 * now; it is recorded as a follow-up (OPEN-QUESTIONS Q1).
 *
 * The consequence to state, and the one the author will not expect, is that the
 * track is open to the whole crew right now — the opposite of what they ticked.
 */
export function ignoredRequirements(tracks: LearnTrack[]): IgnoredRequirement[] {
  const byId: Record<string, LearnTrack> = {};
  for (const t of tracks) byId[t.id] = t;

  const out: IgnoredRequirement[] = [];
  for (const requiring of tracks) {
    const present = requiring.prereq_track_ids.filter((id) => !!byId[id]);
    if (present.length === 0) continue;

    const ignoredTrackIds = present.filter((id) => !isGatingEdge(requiring, byId[id]));
    if (ignoredTrackIds.length === 0) continue;

    out.push({
      trackId: requiring.id,
      reason: requiring.access_mode === 'open' ? 'open_mode' : 'hidden_required',
      ignoredTrackIds,
      gatingTrackIds: present.filter((id) => isGatingEdge(requiring, byId[id])),
    });
  }
  return out;
}

// --- The one aggregate the renderer consumes ------------------------------

export interface CurriculumMap {
  tiers: string[][];
  tierOf: Record<string, number>;
  loopGroups: string[][];
  edges: MapEdge[];
  /** H1 — Lesson ids. */
  unreachableLessonIds: Set<string>;
  /** H2's cause — track ids with zero On Lessons. */
  emptyTrackIds: Set<string>;
  /** H2 — keyed by the track carrying the mark. */
  deadEndByTrack: Record<string, DeadEndChain>;
  /** H3 — keyed by the track carrying the mark. */
  ignoredByTrack: Record<string, IgnoredRequirement>;
  /** H4 — the group each member belongs to, keyed by track id. */
  loopGroupByTrack: Record<string, string[]>;
  /** How many tracks gating-require each track. Drives the empty track's cause
   *  box ("N Tracks require this one"), which is what makes an empty box read as
   *  a problem rather than as a track nobody got to yet. */
  requiredByCount: Record<string, number>;
}

/**
 * Everything the Map renders, derived once. One entry point so a component never
 * assembles a second opinion out of the parts, and so the whole derivation is
 * addressable from one place in a test.
 */
export function buildCurriculumMap(
  tracks: LearnTrack[],
  lessons: MapLesson[],
): CurriculumMap {
  const { tiers, tierOf, loopGroups: groups } = trackTiers(tracks);

  const deadEndByTrack: Record<string, DeadEndChain> = {};
  for (const chain of deadEndChains(tracks, lessons)) deadEndByTrack[chain.trackId] = chain;

  const ignoredByTrack: Record<string, IgnoredRequirement> = {};
  for (const row of ignoredRequirements(tracks)) ignoredByTrack[row.trackId] = row;

  const loopGroupByTrack: Record<string, string[]> = {};
  for (const group of groups) for (const id of group) loopGroupByTrack[id] = group;

  const byId: Record<string, LearnTrack> = {};
  for (const t of tracks) byId[t.id] = t;
  const requiredByCount: Record<string, number> = {};
  for (const t of tracks) {
    for (const requiredId of t.prereq_track_ids) {
      const required = byId[requiredId];
      if (!required || !isGatingEdge(t, required)) continue;
      requiredByCount[requiredId] = (requiredByCount[requiredId] ?? 0) + 1;
    }
  }

  return {
    tiers,
    tierOf,
    loopGroups: groups,
    edges: mapEdges(tracks),
    unreachableLessonIds: unreachableLessonIds(lessons, tracks),
    emptyTrackIds: tracksWithNoOnLessons(tracks, lessons),
    deadEndByTrack,
    ignoredByTrack,
    loopGroupByTrack,
    requiredByCount,
  };
}

/**
 * Move `id` to the index currently held by `targetId` — the reindex both drag
 * lists use, matching `MasterDetailTree`'s splice-in-place semantics exactly so
 * a drag in the Tracks tab feels like a drag in the Lessons tab.
 */
export function moveBefore(ids: string[], id: string, targetId: string): string[] {
  if (id === targetId) return ids;
  const next = [...ids];
  const from = next.indexOf(id);
  const to = next.indexOf(targetId);
  if (from < 0 || to < 0) return ids;
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}
